import { useEffect, useRef, useState } from 'react'
import {
  createPost,
  publishPost,
  publishDraft,
  editPost,
  editLivePost,
  discardPost,
  loadPostBylines,
  type MyByline,
  type PostByline,
  type PublishResult,
  type UnpublishedPost,
} from '../lib/feed'
import {
  preparePhoto,
  cropPhoto,
  uploadPhoto,
  keepOriginal,
  setPhotoUses,
  myPhotoUses,
  myPhotoDetails,
  removePhotoDetail,
  usedOutsideContests,
  ALL_USES,
  NO_USES,
  DETAIL_KEYS,
  photoContestReward,
  extractTags,
  signedPhotoUrl,
  MAX_PHOTOS,
  type PreparedPhoto,
  type AttachmentInput,
  type PhotoUses,
  type PhotoDetails,
} from '../lib/photos'
import { HelpButton } from './HelpButton'
import { PhotoOptOut, optOutSummary } from './PhotoOptOut'
import { enterContest, loadContestList, type Contest } from '../lib/photoContests'
import { CropIcon, ImageIcon, LockIcon, PeopleIcon } from './icons'
import { PhotoTagEditor } from './PhotoTagEditor'
import { FeedPhotoCrop } from './FeedPhotoCrop'
import type { CropShapeKey, NormRect } from '../lib/photoCrop'
import { photoTagsFor, setPhotoTags, type TaggedPerson } from '../lib/photoTags'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'

const MAX_BODY = 5000

/* 161. A text field a password manager should leave alone. LastPass drew its
   red "•••" button inside the headline box (David's screenshot, 11 Oct). */
const NOT_A_LOGIN = { autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true', 'data-form-type': 'other' } as const

/** How many names the photo card spells out before "+N more". */
const NAMES_ON_CARD = 3

/* Enter in a one-line field must not post (David, 9 Oct: Enter in the tag
   search published a half-tagged post). The form's implicit submission fires
   from ANY text input, so the headline and photo-caption fields opt out too;
   the Post button is the one way to post. */
function noEnterSubmit(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Enter') e.preventDefault()
}

/**
 * Write a post, with photos (FED-6 + FND-6), through the guidelines check.
 *
 * ── IT NO LONGER OWNS WHETHER IT IS OPEN. FED-35 ────────────────────────────
 *
 * This used to render its own collapsed prompt — the "Share something with the
 * company" bar that sat permanently in the feed — and flip itself open. Both
 * halves are gone: the feed now has two buttons and this is mounted inside a
 * <Dialog> by the ROUTE.
 *
 * Mounting it from the route is FED-35's own instruction and 059's rule behind
 * it: "Post something" has to mean the same thing everywhere, and a second
 * copy of a create form is how a second copy of the thing gets published. So
 * there is exactly one <Composer> on the hub, and both buttons open it.
 *
 * What it lost is `open`/`setOpen`. What it gained is `draft` and `onClose`.
 * Do not give it back an internal open state — the dialog owns that, and two
 * things owning one boolean is how the drawer got two launchers (HLP-15).
 *
 * ── WHAT THIS DELIBERATELY DOES NOT HAVE ────────────────────────────────────
 *
 * An audience picker. That is 015's split: an ordinary employee holds
 * content.post, which publishes to the DEFAULT audience — an empty
 * content_audience set, meaning everyone in the tenant. Aiming a message at one
 * business unit needs content.publish AND audience.build together, which is an
 * editorial capability and a different screen.
 *
 * So this composer states in words who the post is going to. It does not offer
 * a control that some people can use and others cannot, because a disabled
 * dropdown is a worse explanation than a sentence.
 *
 * ── TAGS ARE READ OUT OF THE TEXT, NOT ASKED FOR TWICE ──────────────────────
 *
 * People type "finished the causeway today #FraserDelta #CPT". A separate tag
 * field would mean typing them again, and half of them would then exist in the
 * body as decoration and not in the field as data. So the composer reads them
 * out of what was written and SHOWS what it found, which is the part that
 * matters — a silent extraction that gets it wrong is worse than no extraction.
 *
 * The normalising (#Fraser_Delta and #FraserDelta being one tag, accents
 * folding so #Peñuelas and #Penuelas agree) happens in app.normalise_tag() in
 * the database, once. This only finds candidates.
 *
 * ── PHOTOS ARE UPLOADED BEFORE THE POST EXISTS ──────────────────────────────
 *
 * Upload → get paths → create_post. There is no content_id to scope the storage
 * path by at upload time, which is why the path is
 * `<tenant>/<person>/<uuid>.jpg` and why the Storage policies check the second
 * segment against the caller. It also means a post that fails to publish leaves
 * orphan objects in the bucket — a real cost, and cheaper than the alternative,
 * which is a published post whose photos are still uploading.
 *
 * A PREFILLED DRAFT'S photos are already up there, so they are carried as
 * `AttachmentInput` from the start and never re-uploaded. That is why the photo
 * list below is two kinds of thing rather than one: `kept` (already in Storage,
 * from the draft) and `added` (a local File this session picked). Only the
 * second kind has a `previewUrl` to revoke.
 *
 * ── THE THREE OUTCOMES ARE THREE DIFFERENT MESSAGES ─────────────────────────
 *
 * The check returns pass, flag or block, and a network failure is a fourth
 * thing. They must not collapse:
 *
 *   pass/flag  the post is live. A flagged author is told NOTHING — guideline 8,
 *              nothing happened to them — so both render the same success.
 *   block      the post does not exist. The author keeps their text, is told
 *              WHICH RULE in plain words, and can appeal. Never "your post
 *              violates our guidelines".
 *   unchecked  the check could not run and the post went out anyway (B09's
 *              fail-open). Said plainly, because the card will carry
 *              "Published unchecked" and the author should not discover that
 *              from the feed.
 *   error      the network failed. Try again — nothing was decided about them.
 */
export function Composer({
  authorName,
  tenantId,
  personId,
  draft,
  live = false,
  bylines = [],
  onPosted,
  onClose,
}: {
  /**
   * 300. The bylines this person may post under (my_bylines). Empty for nearly
   * everyone, and then nothing new is drawn: no "Post as", no disabled
   * control, no empty menu. Offered on a NEW post only — a draft or a live
   * post keeps the byline it was written under.
   */
  bylines?: MyByline[]
  authorName: string
  tenantId: string | null
  personId: string
  /**
   * A draft to carry in, or null for a blank post. FED-35's picker sets this.
   * When it is set the form EDITS that row rather than creating a new one —
   * see `submit`, and note the two paths are genuinely different writes rather
   * than one write with a flag.
   */
  draft: UnpublishedPost | null
  /**
   * 115 (FED-39). `draft` is one of your own LIVE posts rather than a saved
   * draft. The save then goes through editLivePost — the check grades the new
   * words BEFORE they replace the old ones — there is no "save as draft" (a
   * live post does not go back to being one). Add photos IS offered since
   * 144: publish-post grades an edit's new photos and vouches for the ones it
   * saw; edit_post still refuses a new photo the check did not see (David,
   * 9 Oct: removing the only photo left no way to put another in).
   */
  live?: boolean
  /**
   * Called with the content id so the feed can refetch, and with whether the
   * post went out UNSCREENED.
   *
   * The second argument exists because this form now closes on success. B09's
   * fail-open means a post whose check could not run is live anyway, the card
   * will carry "Published unchecked", and the author discovering that from the
   * feed is precisely what this component's header says must not happen — so
   * the fact has to outlive the dialog that learned it. It used to render in
   * the collapsed composer, which no longer exists.
   */
  onPosted: (contentId: string, unchecked: boolean) => void
  onClose: () => void
}) {
  const t = COMPOSE[useHubLang()]
  const [title, setTitle] = useState(draft?.title ?? '')
  const [body, setBody] = useState(draft?.body ?? '')
  /* Already in Storage. Carried through untouched unless the author removes
     one; never re-uploaded, and `bytes` is why migration 094 had to add
     byte_size to my_unpublished_posts — content_attachment.byte_size is NOT
     NULL, so a draft with a photo could be opened and never saved. */
  const [kept, setKept] = useState<AttachmentInput[]>(
    (draft?.attachments ?? []).map((a) => ({
      storage_path: a.path,
      mime_type: a.mime,
      byte_size: a.bytes,
      width: a.width,
      height: a.height,
      alt_text: a.alt,
      exif_stripped: true,
    })),
  )
  const [photos, setPhotos] = useState<PreparedPhoto[]>([])
  /* FED-66. The new photo being cropped (by its preview URL, which is unique
     per version of a photo) and its whole copy, decoded for the dialog. */
  const [cropping, setCropping] = useState<{ url: string; source: ImageBitmap | null } | null>(null)
  const [cropBusy, setCropBusy] = useState(false)
  const [cropError, setCropError] = useState<string | null>(null)
  /* 148 (FED-53). What each photo ALREADY on the draft or live post may be
     used for, read from photo_permission (my_photo_uses). Absent = never
     decided = no use at all (141's rule), shown as "feed only". Only the ones
     the person CHANGES are written back (`usesDirty`): if the read failed,
     writing every kept photo would turn a stored yes into a no nobody chose. */
  const [keptUses, setKeptUses] = useState<Record<string, { uses: PhotoUses; reason: string }>>({})
  const [usesDirty, setUsesDirty] = useState<Set<string>>(new Set())
  const [usesLoaded, setUsesLoaded] = useState(kept.length === 0)
  /* The opt-out dialog, for one photo at a time. */
  const [optingOut, setOptingOut] = useState<
    { kind: 'new'; index: number; label: string } | { kind: 'kept'; path: string; label: string } | null
  >(null)
  /* 149 / 150 (FED-54). Camera details and location are per photo, each on
     its own, in the same opt-out dialog as the uses (David, 10 Oct). New
     photos: PreparedPhoto.details, both on by default, no remembered choice.
     Kept photos: what is held (my_photo_details), and what the person opted
     out here, removed when the post saves. */
  const [keptDetails, setKeptDetails] = useState<Record<string, PhotoDetails>>({})
  const [removeDetails, setRemoveDetails] = useState<Record<string, PhotoDetails>>({})
  /* CMP-7. Themed contests taking entries, offered while a new photo is in
     the contest; ticked ones are entered after the post saves. */
  const [openContests, setOpenContests] = useState<Contest[]>([])
  const [enterIn, setEnterIn] = useState<Set<string>>(new Set())
  /* After the post is up, if an entry was refused: say so here rather than
     close on it (CMP-7: "decide what the composer says"). */
  const [afterPost, setAfterPost] = useState<{ contentId: string; unchecked: boolean; lines: string[] } | null>(null)
  /* 143 (FED-49). Who is tagged in the photos ALREADY on the post. Same rule
     as the contest box: only the photos whose list the person CHANGES are
     written back, so a failed read can never wipe tags nobody touched. */
  const [keptTags, setKeptTags] = useState<Record<string, TaggedPerson[]>>({})
  /* 161. The tag editor, for one photo at a time. */
  const [tagging, setTagging] = useState<{ kind: 'new'; index: number } | { kind: 'kept'; path: string } | null>(null)
  /* 161. Kept photos show as photos now. The comment that said this form had
     no signed URL to show one with predates signedPhotoUrl (141), which the
     feed and the viewer already use; a failed signature leaves a blank tile. */
  const [keptUrls, setKeptUrls] = useState<Record<string, string>>({})
  /* Files dragged over the form. */
  const [dragging, setDragging] = useState(false)
  const [tagsDirty, setTagsDirty] = useState<Set<string>>(new Set())
  const [tagsLoaded, setTagsLoaded] = useState(kept.length === 0)
  /* 142. What a ticked photo earns, shown beside the box while it is ticked.
     From the rule, not a literal, so an admin's change is what people see. */
  const [rewardRule, setReward] = useState<{ stars: number; cap: number } | null>(null)
  /* 300. Who a NEW post goes out as: null = yourself (the default), or a
     byline key. And the byline an existing draft or live post already has,
     read from post_bylines (it is fixed once written). */
  const [postAs, setPostAs] = useState<string | null>(null)
  const [draftByline, setDraftByline] = useState<PostByline | null>(null)
  useEffect(() => {
    if (!draft) return
    let live = true
    loadPostBylines([draft.contentId])
      .then((m) => live && setDraftByline(m.get(draft.contentId) ?? null))
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [confirmTakeDown, setConfirmTakeDown] = useState(false)
  useEffect(() => {
    if (!tenantId) return
    let live = true
    photoContestReward(tenantId)
      .then((r) => live && setReward(r))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [tenantId])
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<Extract<PublishResult, { blocked: true }> | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (kept.length === 0) return
    let live = true
    Promise.all([myPhotoUses(kept.map((a) => a.storage_path)), myPhotoDetails(kept.map((a) => a.storage_path))])
      .then(([m, d]) => {
        if (!live) return
        setKeptUses(Object.fromEntries(m))
        setKeptDetails(Object.fromEntries([...d].map(([k, v]) => [k, { camera: v.camera, location: v.location }])))
        setUsesLoaded(true)
      })
      // Left disabled on a failed read: a box that cannot show the truth
      // should not invite a change on top of it.
      .catch(() => {})
    return () => {
      live = false
    }
    // Once, for the photos the composer opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (kept.length === 0) return
    let live = true
    photoTagsFor(kept.map((a) => a.storage_path))
      .then((rows) => {
        if (!live) return
        const m: Record<string, TaggedPerson[]> = {}
        for (const r of rows) {
          ;(m[r.path] ??= []).push({ personId: r.personId, displayName: r.displayName, x: r.x, y: r.y })
        }
        setKeptTags(m)
        setTagsLoaded(true)
      })
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (kept.length === 0) return
    let live = true
    for (const a of kept) {
      signedPhotoUrl(a.storage_path)
        .then((u) => {
          if (live && u) setKeptUrls((cur) => ({ ...cur, [a.storage_path]: u }))
        })
        .catch(() => {})
    }
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Object URLs are a leak if nobody revokes them, and the composer can churn
  // through a lot of them if somebody keeps swapping photos. Only `photos`
  // hold one — a kept attachment is a path, not a blob.
  //
  // ON UNMOUNT ONLY (FED-66). This used to be a cleanup keyed on `photos`,
  // which runs on EVERY change to the list with the PREVIOUS list — so typing
  // a caption revoked every preview URL still on screen, and the tag editor
  // opened afterwards on a URL that no longer loads. A single photo's URL is
  // revoked where it stops being used: removePhoto, and applyCrop.
  const photosRef = useRef(photos)
  photosRef.current = photos
  useEffect(() => {
    return () => {
      for (const p of photosRef.current) URL.revokeObjectURL(p.previewUrl)
    }
  }, [])

  const trimmed = body.trim()
  const tooLong = body.length > MAX_BODY
  const photoCount = kept.length + photos.length
  // A photo with no words IS a post — most site photos are — so either alone
  // is enough. create_post and edit_post enforce the same rule server-side.
  const hasSomething = trimmed.length > 0 || photoCount > 0
  const canSubmit = hasSomething && !tooLong && !busy && tenantId !== null

  const tags = extractTags(body)
  const isEditing = draft !== null
  const isLive = live && draft !== null
  /* 300. The byline this post is under, whichever way it got one. A byline
     post earns nothing (David, 19 Oct), so the star lines and the themed
     contests are not offered under one. */
  const underByline: string | null = isEditing
    ? (draftByline?.name ?? null)
    : (bylines.find((b) => b.key === postAs)?.name ?? null)
  const reward = underByline ? null : rewardRule
  // A blocked post is NOT re-sendable by its author. 028 made the release a
  // moderator's act on purpose and edit_post refuses one outright, so the
  // picker does not offer it and this is the second half of the same rule.
  const isBlocked = draft?.blockReason !== null && draft?.blockReason !== undefined

  async function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setError(null)
    const room = MAX_PHOTOS - photoCount
    if (room <= 0) {
      setError(t.photoLimit(MAX_PHOTOS))
      return
    }

    setBusy(true)
    setProgress(t.preparingPhotos)
    const prepared: PreparedPhoto[] = []
    for (const file of Array.from(files).slice(0, room)) {
      try {
        prepared.push(await preparePhoto(file))
      } catch (e) {
        // One bad file does not lose the others, and the message names the file
        // rather than saying "an image failed" about a set of nine.
        setError(messageOf(e))
      }
    }
    setPhotos((cur) => [...cur, ...prepared])
    setBusy(false)
    setProgress(null)
  }

  function removePhoto(index: number) {
    setPhotos((cur) => {
      const p = cur[index]
      if (p) URL.revokeObjectURL(p.previewUrl)
      return cur.filter((_, i) => i !== index)
    })
  }

  function setAlt(index: number, alt: string) {
    setPhotos((cur) => cur.map((p, i) => (i === index ? { ...p, altText: alt } : p)))
  }

  function setUses(index: number, uses: PhotoUses, reason: string, details: PhotoDetails) {
    setPhotos((cur) => cur.map((p, i) => (i === index ? { ...p, uses, reason, details } : p)))
  }

  function setKeptAlt(path: string, alt: string) {
    setKept((cur) => cur.map((a) => (a.storage_path === path ? { ...a, alt_text: alt } : a)))
  }

  function setTagged(index: number, tagged: TaggedPerson[]) {
    setPhotos((cur) => cur.map((p, i) => (i === index ? { ...p, tagged } : p)))
  }

  /* FED-66. Crop a NEW photo — never a kept one (FeedPhotoCrop's header). The
     dialog shows the whole 2,000 px copy; the crop comes back as fractions and
     cropPhoto cuts the feed copy from the picked file. The full-size copy is
     cut from the same fractions at upload (keepOriginal). */
  async function openCrop(p: PreparedPhoto) {
    setCropError(null)
    setCropping({ url: p.previewUrl, source: null })
    try {
      const bitmap = await createImageBitmap(p.whole ?? p.file)
      setCropping((c) => (c && c.url === p.previewUrl ? { ...c, source: bitmap } : c))
    } catch {
      setCropping(null)
      setError(t.cropFailed)
    }
  }

  function closeCrop() {
    cropping?.source?.close?.()
    setCropping(null)
    setCropError(null)
  }

  async function applyCrop(rect: NormRect, shape: CropShapeKey) {
    const p = photos.find((x) => x.previewUrl === cropping?.url)
    if (!p) return closeCrop()
    setCropBusy(true)
    setCropError(null)
    try {
      const next = await cropPhoto(p, rect, shape)
      setPhotos((cur) => cur.map((x) => (x.previewUrl === p.previewUrl ? next : x)))
      URL.revokeObjectURL(p.previewUrl)
      closeCrop()
    } catch {
      // The photo is untouched: the new copy replaces it only on success.
      setCropError(t.cropFailed)
    } finally {
      setCropBusy(false)
    }
  }

  function setKeptTagsFor(path: string, tagged: TaggedPerson[]) {
    setKeptTags((cur) => ({ ...cur, [path]: tagged }))
    setTagsDirty((cur) => new Set(cur).add(path))
  }

  /* 143. Written AFTER the post saves: set_photo_tags finds the post the
     photo is on, because the post decides who may see a tag. A draft's tags
     are told when it goes live (server trigger). Never blocks the post — the
     post is already saved, and the viewer shows what actually stuck. */
  async function writeTags(fresh: { path: string; tagged: TaggedPerson[] }[]) {
    const writes = [
      ...fresh.filter((f) => f.tagged.length > 0),
      ...kept
        .filter((a) => tagsDirty.has(a.storage_path))
        .map((a) => ({ path: a.storage_path, tagged: keptTags[a.storage_path] ?? [] })),
    ]
    await Promise.allSettled(
      writes.map((w) => setPhotoTags(w.path, w.tagged)),
    )
  }

  function setKeptUsesFor(path: string, uses: PhotoUses, reason: string) {
    setKeptUses((cur) => ({ ...cur, [path]: { uses, reason } }))
    setUsesDirty((cur) => new Set(cur).add(path))
  }

  /* 148. The kept photos the person changed. Never blocks the post: a failed
     write leaves the old row, and no row means the photo is NOT used — the
     safe direction. New photos are written in the upload loop instead,
     before keepOriginal, because set_photo_location asks the photo's uses. */
  async function writeKeptUses() {
    const writes = kept
      .filter((a) => usesDirty.has(a.storage_path))
      .map((a) => ({ path: a.storage_path, ...(keptUses[a.storage_path] ?? { uses: ALL_USES, reason: '' }) }))
    await Promise.allSettled([
      ...writes.map((w) => setPhotoUses(w.path, w.uses, w.reason)),
      // 150. Kept photos opted out of their camera details or location in the dialog.
      ...kept.flatMap((a) =>
        DETAIL_KEYS.filter((k) => removeDetails[a.storage_path]?.[k]).map((k) => removePhotoDetail(a.storage_path, k)),
      ),
    ])
  }

  /* CMP-7. Load the themed contests taking entries the first time a photo is
     added. The monthly contest needs nothing: the contest use enters it. */
  const wantContests = photos.length > 0
  useEffect(() => {
    if (!wantContests || !tenantId) return
    let live = true
    loadContestList()
      .then((list) => {
        if (!live) return
        setOpenContests(list.filter((c) => c.phase === 'entering' && c.seriesId === null && c.tenantId === tenantId))
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [wantContests, tenantId])

  /* CMP-7. After the post saves: every NEW photo still in the contest goes
     into each ticked themed contest. enter_photo_contest takes a photo whose
     post is not live yet (the entry shows once it is). A refusal — the
     contest's cap, the post's audience — never undoes the post. */
  async function enterChosen(fresh: { path: string; uses: PhotoUses }[]): Promise<string[]> {
    if (underByline) return []
    const chosen = openContests.filter((c) => enterIn.has(c.id))
    const photosIn = fresh.filter((f) => f.uses.contest)
    if (chosen.length === 0 || photosIn.length === 0) return []
    const lines: string[] = []
    const ok: string[] = []
    for (const c of chosen) {
      let entered = 0
      let why: string | null = null
      for (const f of photosIn) {
        try {
          await enterContest(c.id, f.path)
          entered++
        } catch (e) {
          why = messageOf(e)
        }
      }
      if (entered > 0) ok.push(c.title)
      if (why) lines.push(t.notEnteredIn(c.title, why))
    }
    if (lines.length && ok.length) lines.unshift(t.enteredIn(ok))
    return lines
  }

  /* Close on success — unless an entry was refused, which is said here. */
  async function finish(contentId: string, unchecked: boolean, fresh: { path: string; uses: PhotoUses }[]) {
    const lines = await enterChosen(fresh)
    if (lines.length) {
      setAfterPost({ contentId, unchecked, lines })
      return
    }
    onPosted(contentId, unchecked)
    onClose()
  }

  async function submit(publish: boolean) {
    if (!canSubmit || !tenantId) return
    setBusy(true)
    setError(null)
    setBlocked(null)

    try {
      // 161: a kept photo's caption can be edited now; blank means none.
      const uploaded: AttachmentInput[] = kept.map((a) => ({ ...a, alt_text: a.alt_text?.trim() || null }))
      const fresh: { path: string; uses: PhotoUses }[] = []
      const freshTags: { path: string; tagged: TaggedPerson[] }[] = []
      if (photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          setProgress(t.uploadingPhoto(i + 1, photos.length))
          const att = await uploadPhoto(photos[i], tenantId, personId)
          /* 148. The photo's uses, as soon as the file exists (the server
             checks). Never blocks the post: a failed write leaves no row, and
             no row means not used. */
          try {
            // 300: a byline post's photo is never a contest entry, so the
            // record says so rather than a yes nothing will act on.
            const uses = underByline ? { ...photos[i].uses, contest: false } : photos[i].uses
            await setPhotoUses(att.storage_path, uses, photos[i].reason)
          } catch {
            /* the safe direction */
          }
          /* 146 / 149. A photo with a use outside contests also goes up at
             full size, for the photo room; and the location, date and camera
             go with it unless the poster unticked them. After the feed copy
             and the uses, because the server checks both; never fails the
             post (keepOriginal). */
          const keepFull = usedOutsideContests(photos[i].uses)
          const { camera, location } = photos[i].details
          if (keepFull || camera) {
            if (keepFull) setProgress(t.uploadingOriginal(i + 1, photos.length))
            await keepOriginal(photos[i], att.storage_path, { keepFull, camera, location })
          }
          uploaded.push(att)
          fresh.push({ path: att.storage_path, uses: photos[i].uses })
          freshTags.push({ path: att.storage_path, tagged: photos[i].tagged })
        }
      }
      await writeKeptUses()

      /* ── EDITING AN EXISTING DRAFT ────────────────────────────────────────
         Two statements, not one, and deliberately in this order: the words are
         saved FIRST and only then published. publish_draft grades whatever is
         stored, so publishing before saving would send the check the OLD text
         and put the NEW text in front of people unscreened — which is exactly
         the hole migration 094 part C closed one layer down. If the edit
         succeeds and the publish fails, the draft is saved and the author can
         try again; the reverse would be unrecoverable. */
      /* ── EDITING A LIVE POST (115) ─────────────────────────────────────────
         One call. The Edge Function grades the new words and only then applies
         them; a block comes back exactly as a new post's does and NOTHING has
         changed, so the text stays on screen for another try. Since 144
         `uploaded` can carry new photos: they were uploaded above and the
         check grades them with the words. */
      if (isLive && draft) {
        setProgress(t.checkingEdit)
        const res = await editLivePost({
          contentId: draft.contentId,
          body: trimmed,
          title,
          attachments: uploaded,
          tags,
        })
        if (res.blocked) {
          setBlocked(res)
          return
        }
        await writeTags(freshTags)
        await finish(draft.contentId, res.unchecked, fresh)
        return
      }

      if (isEditing && draft) {
        setProgress(t.saving)
        await editPost({
          contentId: draft.contentId,
          body: trimmed,
          title,
          attachments: uploaded,
          tags,
        })
        await writeTags(freshTags)

        if (!publish) {
          await finish(draft.contentId, false, fresh)
          return
        }

        setProgress(t.checkingPost)
        const res = await publishDraft(draft.contentId)
        if (res.blocked) {
          setBlocked(res)
          return
        }
        await finish(draft.contentId, res.unchecked, fresh)
        return
      }

      if (!publish) {
        // A draft is not in front of anyone, so it is not checked and goes
        // straight to create_post.
        setProgress(t.saving)
        const id = await createPost({
          body: trimmed,
          title,
          publish: false,
          attachments: uploaded,
          tags,
          byline: postAs,
        })
        await writeTags(freshTags)
        // A draft is not in front of anybody, so it is not unscreened — it is
        // unchecked in the ordinary sense and that is not news.
        await finish(id, false, fresh)
        return
      }

      setProgress(photos.length > 0 ? t.checkingPostAndPhotos : t.checkingPost)
      const res = await publishPost({ body: trimmed, title, attachments: uploaded, tags, byline: postAs })

      if (res.blocked) {
        // Guideline 8: keep their text on screen, tell them which rule. Do NOT
        // close — throwing away what they wrote because a machine said no is
        // the behaviour this whole section exists to prevent.
        setBlocked(res)
        return
      }

      await writeTags(freshTags)

      // pass, flag or failed_open. The dialog closes and Home says what
      // happened, because an unchecked post's notice has to outlive the
      // surface that produced it — it used to render inside the collapsed
      // composer, which no longer exists to render anything.
      await finish(res.contentId, res.unchecked, fresh)
    } catch (e) {
      // A network failure is NOT a block. Different words, because telling
      // somebody their connection failed when they were stopped — or the
      // reverse — is the confusion worth spending code to avoid.
      setError(messageOf(e))
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  /* CMP-7. The post is up but an entry was refused: say which, then close. */
  if (afterPost) {
    return (
      <div className="composer composer--done" role="status">
        <p className="composer__who">{t.postIsUp}</p>
        {afterPost.lines.map((l) => (
          <p key={l} className="composer__mediahint">{l}</p>
        ))}
        <div className="composer__media">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              onPosted(afterPost.contentId, afterPost.unchecked)
              onClose()
            }}
          >
            {t.close}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form
      className={`composer${dragging ? ' composer--dragging' : ''}`}
      onSubmit={(e) => {
        e.preventDefault()
        submit(true)
      }}
      /* 161. Photos can be dropped anywhere on the form. */
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes('Files') || busy) return
        e.preventDefault()
        if (!dragging) setDragging(true)
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
      }}
      onDrop={(e) => {
        if (!Array.from(e.dataTransfer.types).includes('Files')) return
        e.preventDefault()
        setDragging(false)
        if (!busy) void addFiles(e.dataTransfer.files)
      }}
    >
      <p className="composer__who">
        <span className={`avatar${underByline ? ' avatar--onbrand' : ''}`} aria-hidden="true">
          {initialsOf(underByline ?? authorName)}
        </span>
        <span>
          {isLive
            ? underByline ? t.editingLiveUnder(underByline) : t.editingLive
            : isEditing
              ? underByline ? t.editingDraftUnder(underByline) : t.editingDraft
              : t.postingAs(underByline ?? authorName)}
        </span>
      </p>

      {/* 300. Only for someone who holds a byline, and only on a new post. */}
      {!isEditing && bylines.length > 0 && (
        <div className="composer__postas">
          <label className="composer__label" htmlFor="composer-postas">
            {t.postAsLabel}
          </label>
          <select
            id="composer-postas"
            className="composer__select"
            value={postAs ?? ''}
            onChange={(e) => setPostAs(e.target.value || null)}
            disabled={busy}
          >
            <option value="">{t.postAsMe(authorName)}</option>
            {bylines.map((b) => (
              <option key={b.key} value={b.key}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {underByline && <p className="composer__mediahint composer__bylinenote">{t.bylineNote(underByline, authorName)}</p>}

      <label className="composer__label" htmlFor="composer-title">
        {t.headline} <span className="composer__optional">{t.optional}</span>
      </label>
      <input
        id="composer-title"
        className="composer__title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={noEnterSubmit}
        placeholder={t.headlinePlaceholder}
        maxLength={200}
        disabled={busy}
        {...NOT_A_LOGIN}
      />

      <label className="composer__label" htmlFor="composer-body">
        {t.yourPost}
      </label>
      <textarea
        id="composer-body"
        className="composer__body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={t.bodyPlaceholder}
        rows={5}
        disabled={busy}
        autoFocus
        aria-describedby="composer-audience"
      />

      {/* Only near the limit. A counter at "0 / 5000" under an empty box reads
          as an instruction to write 5000 characters. */}
      {body.length > MAX_BODY * 0.8 && (
        <p className={`composer__count${tooLong ? ' composer__count--over' : ''}`}>
          {body.length.toLocaleString()} / {MAX_BODY.toLocaleString()}
        </p>
      )}

      {/* Show what was found. A silent extraction that gets it wrong is worse
          than none — this is the confirmation, not decoration. */}
      {tags.length > 0 && (
        <p className="composer__tags">
          {t.tags} {tags.map((t) => (
            <span key={t} className="tagchip">
              {t}
            </span>
          ))}
        </p>
      )}

      {(kept.length > 0 || photos.length > 0) && (
        /* 161. One card per photo: the photo itself (a button into the tag
           editor), its caption, who is in it, and what it may be used for.
           Kept photos are cards like new ones — they used to be a text row
           that fell into the new photos' three-column grid. */
        <ul className="composer__photos">
          {kept.map((a, i) => {
            const n = i + 1
            const label = isLive ? t.liveKeptPhoto(n) : t.keptPhoto(n)
            // Absent = never decided = no use (141), shown as feed only.
            const k = keptUses[a.storage_path] ?? { uses: NO_USES, reason: '' }
            // "no camera details" / "no location" only when the person
            // opted them out here — a photo that never had any is not news.
            const rm = removeDetails[a.storage_path]
            const summary = optOutSummary(t, k.uses, { camera: !rm?.camera, location: !rm?.location })
            return (
              <PhotoCard
                key={a.storage_path}
                label={label}
                src={keptUrls[a.storage_path] ?? null}
                caption={a.alt_text ?? ''}
                onCaption={(v) => setKeptAlt(a.storage_path, v)}
                captionLabel={t.photoDescription(n)}
                tagged={keptTags[a.storage_path] ?? []}
                onTag={() => setTagging({ kind: 'kept', path: a.storage_path })}
                tagLabel={t.tagPeopleFor(n)}
                tagDisabled={busy || !tagsLoaded}
                onRemove={() => setKept((cur) => cur.filter((_, j) => j !== i))}
                removeLabel={isLive ? t.liveRemoveKeptPhoto(n) : t.removeKeptPhoto(n)}
                busy={busy}
              >
                <div className="composer__uses">
                  {summary && <span className="composer__optedout">{summary}</span>}
                  <button
                    type="button"
                    className="btn btn--link composer__optout"
                    onClick={() => setOptingOut({ kind: 'kept', path: a.storage_path, label: a.alt_text || label })}
                    disabled={busy || !usesLoaded}
                    aria-label={summary ? `${summary}. ${t.optOutChange}` : t.optOutFor(n)}
                  >
                    {summary ? t.optOutChange : t.optOutLink}
                  </button>
                  {/* A live post's photos were paid (or not) when it went live;
                      only a draft's still have stars ahead of them. */}
                  {!isLive && reward && k.uses.licence && (
                    <span className="composer__earn">{t.earnStars(reward.stars)}</span>
                  )}
                </div>
              </PhotoCard>
            )
          })}
          {photos.map((p, i) => {
            const n = kept.length + i + 1
            const summary = optOutSummary(t, p.uses, p.details)
            return (
              <PhotoCard
                key={p.previewUrl}
                label={t.photoCard(n, photoCount)}
                src={p.previewUrl}
                /* Asked for, never invented. PhotoGrid renders alt="" when this
                   is blank, which tells a screen reader to skip it — a made-up
                   description would be a lie delivered confidently. */
                caption={p.altText}
                onCaption={(v) => setAlt(i, v)}
                captionLabel={t.photoDescription(n)}
                tagged={p.tagged}
                onTag={() => setTagging({ kind: 'new', index: i })}
                tagLabel={t.tagPeopleFor(n)}
                tagDisabled={busy}
                onRemove={() => removePhoto(i)}
                removeLabel={t.removePhoto(n)}
                onCrop={() => void openCrop(p)}
                cropLabel={t.cropFor(n)}
                busy={busy || cropping !== null}
              >
                <div className="composer__uses">
                  {summary && <span className="composer__optedout">{summary}</span>}
                  <button
                    type="button"
                    className="btn btn--link composer__optout"
                    onClick={() => setOptingOut({ kind: 'new', index: i, label: p.altText || t.photoOf(n, photoCount) })}
                    disabled={busy}
                    aria-label={summary ? `${summary}. ${t.optOutChange}` : t.optOutFor(n)}
                  >
                    {summary ? t.optOutChange : t.optOutLink}
                  </button>
                  {/* 150: the licence earns the stars, not the contest. */}
                  {reward && p.uses.licence && (
                    <span className="composer__earn">{t.earnStars(reward.stars)}</span>
                  )}
                </div>
              </PhotoCard>
            )
          })}
        </ul>
      )}

      {/* FED-66. The crop, over this dialog, for one new photo. */}
      {cropping && (() => {
        const p = photos.find((x) => x.previewUrl === cropping.url)
        if (!p) return null
        return (
          <FeedPhotoCrop
            source={cropping.source}
            initialShape={p.cropShape ?? 'original'}
            initialRect={p.crop ?? null}
            pinned={p.tagged.some((g) => g.x !== null)}
            busy={cropBusy}
            error={cropError}
            onUse={(rect, shape) => void applyCrop(rect, shape)}
            onCancel={closeCrop}
            t={{ dialog: t.cropCopy, shape: t.cropShape, shapes: t.cropShapes, tagsNote: t.cropTagsNote }}
          />
        )
      })()}

      {/* 161. The tag editor, over this dialog, for one photo. */}
      {tagging && (() => {
        if (tagging.kind === 'new') {
          const p = photos[tagging.index]
          if (!p) return null
          return (
            <PhotoTagEditor
              src={p.previewUrl}
              width={p.width}
              height={p.height}
              title={t.tagPeopleFor(kept.length + tagging.index + 1)}
              value={p.tagged}
              onDone={(next) => {
                setTagged(tagging.index, next)
                setTagging(null)
              }}
              onClose={() => setTagging(null)}
            />
          )
        }
        const i = kept.findIndex((a) => a.storage_path === tagging.path)
        const a = kept[i]
        if (!a) return null
        return (
          <PhotoTagEditor
            src={keptUrls[a.storage_path] ?? null}
            width={a.width}
            height={a.height}
            title={t.tagPeopleFor(i + 1)}
            value={keptTags[a.storage_path] ?? []}
            onDone={(next) => {
              setKeptTagsFor(a.storage_path, next)
              setTagging(null)
            }}
            onClose={() => setTagging(null)}
          />
        )
      })()}

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => {
          addFiles(e.target.files)
          // Reset, or picking the same file twice in a row does nothing.
          e.target.value = ''
        }}
      />
      <div className={`composer__media${dragging ? ' is-dragging' : ''}`}>
        <button
          type="button"
          className="btn btn--quiet composer__addphotos"
          onClick={() => fileInput.current?.click()}
          disabled={busy || photoCount >= MAX_PHOTOS}
        >
          <ImageIcon size={16} />
          {t.addPhotos}
        </button>
        {dragging ? (
          <span className="composer__mediahint">{t.dropPhotos}</span>
        ) : (
          <span className="composer__mediahint">
            {photoCount < MAX_PHOTOS && <span className="composer__draghint">{t.dragHint} · </span>}
            {photoCount > 0 ? t.photoCount(photoCount, MAX_PHOTOS) : t.photoHint}
          </span>
        )}
      </div>

      {/* 148 (FED-53, David 10 Oct): one line, the rest behind the ?. The
          wording is versioned server-side (notice_version). */}
      {photoCount > 0 && (
        <div className="composer__contestnote">
          {t.photoLine}{' '}
          <HelpButton title={t.photoHelpTitle}>
            {t.photoHelp(reward && !isLive ? { n: reward.stars, cap: reward.cap } : null).map((para) => (
              <p key={para}>{para}</p>
            ))}
          </HelpButton>
        </div>
      )}
      {/* CMP-7. Themed contests taking entries, while a new photo is in the
          contest. The monthly contest needs nothing. */}
      {!underByline && openContests.length > 0 && photos.some((p) => p.uses.contest) && (
        <fieldset className="composer__contests">
          <legend>{t.alsoEnter}</legend>
          {openContests.map((c) => (
            <label key={c.id} className="composer__feature">
              <input
                type="checkbox"
                checked={enterIn.has(c.id)}
                onChange={(e) =>
                  setEnterIn((cur) => {
                    const n = new Set(cur)
                    if (e.target.checked) n.add(c.id)
                    else n.delete(c.id)
                    return n
                  })
                }
                disabled={busy}
              />
              <span>{c.title}</span>
            </label>
          ))}
          <p className="composer__mediahint">{t.alsoEnterHint}</p>
        </fieldset>
      )}
      {optingOut && (
        <PhotoOptOut
          open
          photoLabel={optingOut.label}
          uses={
            optingOut.kind === 'new'
              ? photos[optingOut.index]?.uses ?? ALL_USES
              : keptUses[optingOut.path]?.uses ?? NO_USES
          }
          reason={optingOut.kind === 'new' ? photos[optingOut.index]?.reason ?? '' : keptUses[optingOut.path]?.reason ?? ''}
          details={(() => {
            if (optingOut.kind === 'new') return photos[optingOut.index]?.details ?? { camera: true, location: true }
            const held = keptDetails[optingOut.path] ?? { camera: false, location: false }
            const rm = removeDetails[optingOut.path]
            return { camera: held.camera && !rm?.camera, location: held.location && !rm?.location }
          })()}
          detailsAvailable={
            optingOut.kind === 'new'
              ? { camera: true, location: true }
              : keptDetails[optingOut.path] ?? { camera: false, location: false }
          }
          licenceStars={reward && !isLive ? reward.stars : null}
          onDone={(uses, reason, details) => {
            if (optingOut.kind === 'new') setUses(optingOut.index, uses, reason, details)
            else {
              setKeptUsesFor(optingOut.path, uses, reason)
              const path = optingOut.path
              const held = keptDetails[path] ?? { camera: false, location: false }
              setRemoveDetails((cur) => ({
                ...cur,
                [path]: { camera: held.camera && !details.camera, location: held.location && !details.location },
              }))
            }
            setOptingOut(null)
          }}
          onClose={() => setOptingOut(null)}
        />
      )}

      {/* No video button, and that is 019's decision rather than an oversight:
          Supabase Storage has no adaptive bitrate, so a phone clip pushed at a
          crew on one bar is a spinner. Employee video is FND-8's own issue. */}

      <p className="composer__audience" id="composer-audience">
        {t.audience[0]}<strong>{t.audience[1]}</strong>{t.audience[2]}
      </p>

      <p className="composer__note">
        <LockIcon size={12} />
        {isLive ? t.liveNote : t.checkedNote}
      </p>

      {blocked && (
        <div className="composer__blocked" role="alert">
          <p className="composer__blockedtitle">{isLive ? t.editNotSaved : t.notPublished}</p>
          <p className="composer__blockedreason">{blocked.reason}</p>
          {blocked.rulesHit.length > 0 && (
            <p className="composer__blockedrules">
              {t.guidelines(blocked.rulesHit.join(', '))}
            </p>
          )}
          <p className="composer__blockedappeal">
            {isLive ? t.editBlockedAppeal : t.blockedAppeal}
          </p>
        </div>
      )}

      {error && (
        <p className="composer__error" role="alert">
          {error}
        </p>
      )}

      {progress && (
        <p className="composer__progress" role="status">
          {progress}
        </p>
      )}

      {/* 144. Save greys out when the edit would leave nothing, and used to say
          nothing about why (David, 9 Oct). */}
      {isEditing && !hasSomething && !busy && (
        <p className="composer__progress" role="status">{t.needsSomething}</p>
      )}

      <div className="composer__actions">
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={!canSubmit || isBlocked}
        >
          {busy ? t.working : isLive ? t.saveChanges : t.post}
        </button>
        {!isLive && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => submit(false)}
            disabled={!canSubmit}
          >
            {isEditing ? t.saveDraft : t.saveAsDraft}
          </button>
        )}
        <button type="button" className="btn btn--quiet" onClick={onClose} disabled={busy}>
          {t.cancel}
        </button>
        {/* 300 (decision 3). A live post under a byline can be taken down by
            anyone who holds it, so nothing is stuck while its author is away.
            discard_post decides; this is the same removal an author's is. */}
        {isLive && draft && draftByline?.iHold && !confirmTakeDown && (
          <button
            type="button"
            className="btn btn--quiet composer__takedown"
            onClick={() => setConfirmTakeDown(true)}
            disabled={busy}
          >
            {t.takeDown}
          </button>
        )}
      </div>
      {isLive && draft && draftByline?.iHold && confirmTakeDown && (
        <div className="composer__confirm" role="alert">
          <p>{t.takeDownConfirm(draftByline.name)}</p>
          <div className="composer__actions">
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                setError(null)
                setProgress(t.takingDown)
                try {
                  await discardPost(draft.contentId)
                  onPosted(draft.contentId, false)
                  onClose()
                } catch (e) {
                  setError(messageOf(e))
                } finally {
                  setBusy(false)
                  setProgress(null)
                }
              }}
            >
              {t.takeDownYes}
            </button>
            <button type="button" className="btn btn--quiet" onClick={() => setConfirmTakeDown(false)} disabled={busy}>
              {t.takeDownNo}
            </button>
          </div>
        </div>
      )}
    </form>
  )
}

/**
 * 161. One photo in the composer: the photo (press it to tag people), a
 * caption, who is in it, and — as children — what it may be used for.
 */
function PhotoCard({
  label,
  src,
  caption,
  onCaption,
  captionLabel,
  tagged,
  onTag,
  tagLabel,
  tagDisabled,
  onRemove,
  removeLabel,
  onCrop,
  cropLabel,
  busy,
  children,
}: {
  label: string
  src: string | null
  caption: string
  onCaption: (v: string) => void
  captionLabel: string
  tagged: TaggedPerson[]
  onTag: () => void
  tagLabel: string
  tagDisabled: boolean
  onRemove: () => void
  removeLabel: string
  /** FED-66. New photos only: a kept photo is already a stored file. */
  onCrop?: () => void
  cropLabel?: string
  busy: boolean
  children: React.ReactNode
}) {
  const t = COMPOSE[useHubLang()]
  const names = tagged.slice(0, NAMES_ON_CARD).map((p) => p.displayName)
  const more = tagged.length - names.length
  return (
    <li className="pcard">
      <button type="button" className="pcard__thumb" onClick={onTag} disabled={tagDisabled} aria-label={tagLabel}>
        {src ? <img src={src} alt="" /> : <span className="pcard__blank" aria-hidden="true" />}
        <span className="pcard__tagme" aria-hidden="true">
          <PeopleIcon size={14} />
          {tagged.length > 0 ? tagged.length : null}
        </span>
      </button>
      <div className="pcard__main">
        <div className="pcard__head">
          <span className="pcard__label">{label}</span>
          <span className="pcard__acts">
            {onCrop && (
              <button type="button" className="pcard__act" onClick={onCrop} disabled={busy} aria-label={cropLabel}>
                <CropIcon size={14} />
                {t.crop}
              </button>
            )}
            <button type="button" className="pcard__remove" onClick={onRemove} disabled={busy} aria-label={removeLabel}>
              {t.remove}
            </button>
          </span>
        </div>
        <input
          className="composer__alt"
          value={caption}
          onChange={(e) => onCaption(e.target.value)}
          onKeyDown={noEnterSubmit}
          placeholder={t.describePhoto}
          maxLength={300}
          disabled={busy}
          aria-label={captionLabel}
          {...NOT_A_LOGIN}
        />
        <div className="pcard__tags">
          {tagged.length > 0 && (
            <span className="pcard__with">
              {t.taggedWith} <strong>{names.join(', ')}</strong>
              {more > 0 ? ` ${t.tagMore(more)}` : ''}
            </span>
          )}
          <button type="button" className="btn btn--link pcard__tagbtn" onClick={onTag} disabled={tagDisabled} aria-label={tagLabel}>
            <PeopleIcon size={14} />
            {tagged.length > 0 ? t.tagEdit : t.tagPeople}
          </button>
        </div>
        {children}
      </div>
    </li>
  )
}

/** Same rule as everywhere else: Array.from, because HR names are not ASCII. */
function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = Array.from(parts[0])[0] ?? ''
  const last = parts.length > 1 ? (Array.from(parts[parts.length - 1])[0] ?? '') : ''
  return (first + last).toUpperCase()
}
